import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/base/Modal";
import {
  inputClass,
  labelClass,
  ghostButtonClass,
  primaryButtonClass,
} from "@/components/base/fieldStyles";
import { formatCOP, formatNumber } from "@/utils/format";
import type { Encargo, EncargoDeliverMode, ExpenseCategory, Wallet } from "@/types";
import type { EncargoDeliveryInput, PurchaseToStockInput } from "@/store/AppStore";

const WALLETS: Wallet[] = ["Efectivo", "Bancolombia", "Daviplata", "Nequi", "Caja Social"];
// Tipo de compra (no fondos): una compra se clasifica por su motivo.
const PURCHASE_CATEGORIES: ExpenseCategory[] = [
  "Compra por encargo",
  "Compra por reposición",
  "Compra por aumento de stock",
  "Gasto operativo",
];

const PAYMENTS: { key: PurchaseToStockInput["payment"]; label: string; icon: string }[] = [
  { key: "pendiente", label: "Dejar pendiente", icon: "ri-time-line" },
  { key: "caja", label: "Pagar con Caja Menor", icon: "ri-safe-2-line" },
];

interface EncargoDeliveryModalProps {
  open: boolean;
  encargo: Encargo | null;
  margin: number;
  costBase: number;
  pricePublic: number;
  stockCasa: number; // unidades disponibles en Oficina
  cajaBalance?: number;
  onClose: () => void;
  onSubmit: (input: EncargoDeliveryInput) => void;
  onBuy: (input: PurchaseToStockInput) => void;
}

// Al marcar un pedido como entregado, el admin decide si el asesor lo COMPRA
// (pagado, va solo a su inventario propio) o queda en CONSIGNACIÓN (va a su
// consignación y sigue contando como inventario del admin).
// REGLA (F5): no se puede entregar sin stock en Oficina. Si no hay, el modal
// obliga a comprarlo primero (ingreso a stock) y habilita recién la entrega.
const EncargoDeliveryModal = ({
  open,
  encargo,
  margin,
  costBase,
  pricePublic,
  stockCasa,
  cajaBalance = 0,
  onClose,
  onSubmit,
  onBuy,
}: EncargoDeliveryModalProps) => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<EncargoDeliverMode>("consignacion");
  const [category, setCategory] = useState<ExpenseCategory>("Compra por encargo");
  const [wallet, setWallet] = useState<Wallet>("Efectivo");
  const [payment, setPayment] = useState<PurchaseToStockInput["payment"]>("pendiente");

  useEffect(() => {
    if (!open) return;
    setMode("consignacion");
    setCategory("Compra por encargo");
    setWallet("Efectivo");
    setPayment("pendiente");
  }, [open]);

  if (!open || !encargo) return null;

  const qty = encargo.qty;
  const fee = encargo.logisticsFee;
  const base = costBase || 0;
  const advisorCost = pricePublic > 0 ? Math.round(pricePublic * (1 - margin / 100)) : 0;
  const profitTotal = (advisorCost - base) * qty;

  const hasStock = stockCasa >= qty;
  const missing = Math.max(0, qty - stockCasa);
  const buyAmount = missing * base;
  const cajaShort = payment === "caja" && missing > 0 && buyAmount > cajaBalance;

  const MODES: { key: EncargoDeliverMode; label: string; hint: string; icon: string }[] = [
    {
      key: "compra",
      label: "Compra (pagada)",
      hint: "El asesor la paga de una vez. Va solo a su inventario propio.",
      icon: "ri-shopping-bag-3-line",
    },
    {
      key: "consignacion",
      label: "Consignación",
      hint: "Sigue siendo tu inventario y suma a la consignación del asesor.",
      icon: "ri-hand-heart-line",
    },
  ];

  const submit = () => {
    if (!hasStock) return;
    onSubmit({ encargoId: encargo.id, mode });
  };

  const buy = () => {
    if (missing <= 0) return;
    onBuy({
      productId: encargo.productId,
      qty: missing,
      unitCost: base,
      category,
      wallet,
      payment,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Entregar pedido por encargo"
      icon="ri-hand-heart-line"
      size="lg"
      footer={
        <>
          <button onClick={onClose} className={ghostButtonClass}>
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={!hasStock}
            className={`${primaryButtonClass} ${!hasStock ? "opacity-40 cursor-not-allowed" : ""}`}
          >
            <i className="ri-check-double-line" /> Confirmar entrega
          </button>
        </>
      }
    >
      <div className="rounded-lg border border-background-300/40 bg-background-200/30 p-3 mb-4">
        <p className="text-sm font-semibold text-foreground-950">
          {encargo.advisorName} · {formatNumber(qty)} x {encargo.productName}
        </p>
        <p className="text-[11px] text-foreground-500 mt-0.5">
          {encargo.delivery === "domicilio" ? "Entrega a domicilio" : "Recojo en tienda"}
          {fee > 0 ? ` · fee logístico ${formatCOP(fee)}` : ""}
        </p>
      </div>

      {/* Candado de stock (F5): sin stock en Oficina no se entrega. */}
      <div
        className={`rounded-lg border p-3 mb-4 ${
          hasStock
            ? "border-accent-500/40 bg-accent-500/10"
            : "border-primary-500/50 bg-primary-500/10"
        }`}
      >
        <div className="flex items-center gap-2">
          <span
            className={`w-8 h-8 rounded-md flex items-center justify-center ${
              hasStock ? "bg-accent-500/20 text-accent-200" : "bg-primary-500/20 text-primary-100"
            }`}
          >
            <i className={hasStock ? "ri-checkbox-circle-line" : "ri-alert-line"} />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground-950">
              Stock en Oficina: {formatNumber(stockCasa)} und · necesitas {formatNumber(qty)} und
            </p>
            <p className="text-[11px] text-foreground-600">
              {hasStock
                ? "Hay stock suficiente. Puedes entregar."
                : `Faltan ${formatNumber(missing)} und. Debes comprarlo e ingresarlo a stock antes de entregar.`}
            </p>
          </div>
        </div>

        {!hasStock && (
          <div className="mt-3 pt-3 border-t border-background-300/30">
            <p className="text-[11px] font-semibold text-foreground-700 mb-2">
              Comprar las {formatNumber(missing)} und faltantes
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {PAYMENTS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPayment(p.key)}
                  className={`rounded-md border px-2.5 py-2 text-left cursor-pointer transition-colors ${
                    payment === p.key
                      ? "border-accent-500/50 bg-accent-500/10"
                      : "border-background-300/50 hover:border-accent-500/30"
                  }`}
                >
                  <span className="flex items-center gap-1.5 text-[10px] font-semibold text-foreground-900">
                    <i className={`${p.icon} text-accent-200`} /> {p.label}
                  </span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              <div>
                <label className={labelClass}>Categoría de la compra</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as ExpenseCategory)}
                  className={inputClass}
                >
                  {PURCHASE_CATEGORIES.map((c) => (
                    <option key={c} value={c} className="bg-background-100">
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Billetera</label>
                <select
                  value={wallet}
                  onChange={(e) => setWallet(e.target.value as Wallet)}
                  className={inputClass}
                >
                  {WALLETS.map((w) => (
                    <option key={w} value={w} className="bg-background-100">
                      {w}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {cajaShort && (
              <div className="mt-2 rounded-md bg-primary-500/12 border border-primary-400/40 px-3 py-2.5 flex items-start gap-2 animate-fade-up">
                <i className="ri-alert-line text-primary-100 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-foreground-950">
                    La Caja Menor no tiene fondos suficientes
                  </p>
                  <p className="text-[10px] text-foreground-700 mt-0.5">
                    Disponible {formatCOP(cajaBalance)} · necesitas {formatCOP(buyAmount)}. Pide un
                    préstamo a Juank (entra a la Caja Menor) para poder comprar.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate("/admin/cartera");
                    }}
                    className="mt-1.5 px-3 py-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer inline-flex items-center gap-1.5"
                  >
                    <i className="ri-exchange-dollar-line" /> Ir a pedir préstamo a Juank
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={buy}
              disabled={cajaShort}
              className={`mt-2 w-full px-3 py-2 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-xs font-semibold whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-1.5 ${
                cajaShort ? "opacity-40 cursor-not-allowed" : ""
              }`}
            >
              <i className="ri-add-circle-line" /> Comprar e ingresar a stock ({formatCOP(buyAmount)})
            </button>
          </div>
        )}
      </div>

      <div className="mb-4">
        <label className={labelClass}>¿Cómo se entrega?</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              className={`rounded-md border px-3 py-3 text-left cursor-pointer transition-colors ${
                mode === m.key
                  ? "border-accent-500/60 bg-accent-500/10"
                  : "border-background-300/50 hover:border-accent-500/30"
              }`}
            >
              <span className="flex items-center gap-2">
                <i className={`${m.icon} text-base ${mode === m.key ? "text-accent-200" : "text-foreground-500"}`} />
                <span className="text-sm font-semibold text-foreground-950">{m.label}</span>
              </span>
              <span className="block text-[10px] text-foreground-500 mt-1">{m.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-background-300/40 bg-background-100/50 p-3">
        <p className="text-[11px] font-semibold text-foreground-600 mb-2 flex items-center gap-1.5">
          <i className="ri-calculator-line text-accent-300" /> Desglose de este producto
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500">Precio público</p>
            <p className="text-sm font-bold text-foreground-950">{formatCOP(pricePublic)}</p>
          </div>
          <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500">Tu costo base</p>
            <p className="text-sm font-bold text-foreground-950">{formatCOP(base)}</p>
          </div>
          <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500">Costo asesor · dto. {margin}%</p>
            <p className="text-sm font-bold text-foreground-950">{formatCOP(advisorCost)}</p>
          </div>
          <div className="rounded-md bg-background-100/60 border border-background-300/40 px-3 py-2">
            <p className="text-[10px] text-foreground-500">Le cobras al asesor</p>
            <p className="text-sm font-bold text-foreground-950">{formatCOP(advisorCost * qty)}</p>
          </div>
          <div className="rounded-md bg-accent-500/10 border border-accent-500/30 px-3 py-2">
            <p className="text-[10px] text-accent-300">Tu ganancia</p>
            <p className="text-sm font-bold text-accent-200">{formatCOP(profitTotal)}</p>
          </div>
        </div>
        {fee > 0 && (
          <p className="text-[10px] text-foreground-500 mt-2 flex items-center gap-1.5">
            <i className="ri-truck-line text-accent-300" /> El fee logístico de {formatCOP(fee)} se le
            cobra al asesor y suma a lo que debe.
          </p>
        )}
      </div>

      <p className="text-[10px] text-foreground-500 mt-3">
        {mode === "compra"
          ? "La mercancía sale de Oficina a tu inventario propio del asesor y el pago queda marcado como recibido. No se registra un gasto nuevo aquí: la compra ya se ingresó a stock."
          : "La mercancía sale de Oficina a la consignación del asesor (sigue contando como tu inventario). El asesor te paga cuando la venda."}
      </p>
    </Modal>
  );
};

export default EncargoDeliveryModal;